import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import 'styles/AgroDataEdit.css'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import AgroDataEditModal from 'components/modals/AgroDataEditModal'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'

export default function AgroDataEdit () {
  const hasFetched = useRef(false)
  const currentFilterDate = useRef(null)
  const { user } = useAuth()
  const [state, setState] = useState({
    agroData: [],
    currentDate: parseDate(new Date()),
    filterDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    searchTerm: '',
    showEditModal: false,
    selectedAgroData: null
  })

  const getAgroData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/agro-data/${user.farmId}?filterDate=${new Date(state.filterDate).toISOString()}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      setState(s => ({ ...s, agroData: response.data }))
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const toggleModal = (agroData = null) => {
    setState(s => ({
      ...s,
      showEditModal: !s.showEditModal,
      selectedAgroData: agroData
    }))
  }

  const handleEditAgroData = async (agroData) => {
    try {
      // Get the original data to compare against
      // const originalData = state.selectedAgroData

      if (agroData?.createdAt.length === 0) {
        showAlert({
          text: 'No changes detected',
          icon: 'info'
        })
        toggleModal()
        return
      }

      const data = {
        createdAt: agroData.createdAt
      }

      const response = await makeRequest({
        method: 'PATCH',
        url: `/management/data-edit/agro-data/${agroData.agroDataId}`,
        data,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        alertResult: true
      })

      if (response.status === 200) {
        showAlert({
          text: 'Agro data updated successfully',
          icon: 'success'
        })

        // Close modal and refresh data
        toggleModal()
        await getAgroData()
      }
    } catch (error) {
      showAlert({
        text: 'Failed to update agro data',
        icon: 'error'
      })
      console.error('Error updating agro data:', error)
    }
  }

  const filterAgroData = state.agroData.filter(data =>
    data.rowCode.toLowerCase().includes(state.searchTerm) ||
    data.activityName.toLowerCase().includes(state.searchTerm)
  )

  useEffect(() => {
    if (hasFetched.current && currentFilterDate.current === state.filterDate) return
    hasFetched.current = true
    currentFilterDate.current = state.filterDate
    getAgroData()
  }, [state.filterDate])

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>Today ({state.currentDate})</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <input
          type='date'
          value={state.filterDate}
          onChange={e => setState(s => ({ ...s, filterDate: e.target.value }))}
          className='w-40 mt-2 mb-2 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
      </div>

      <div className='data-table'>
        {state.agroData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'rowCode', label: 'Barcode Number' },
                { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
                { key: 'activityName', label: 'Activity Name' },
                { key: 'shared', label: 'Shared', cell: (value) => value ? 'Yes' : 'No' },
                { key: 'workerName', label: 'Workers Names' },
                { key: 'blockName', label: 'Block' },
                { key: 'location', label: 'Location' },
                { key: 'actions', label: 'Manage' }
              ],
              rows: filterAgroData
            }}
            handleEdit={toggleModal}
            defaultSortColumn='rowCode'
          />
        )}
      </div>

      <AgroDataEditModal
        isOpen={state.showEditModal}
        handleClose={toggleModal}
        handleSave={handleEditAgroData}
        selectedAgroData={state.selectedAgroData}
      />
    </div>
  )
}
